import React, { useState } from "react";
import { submitLeadQuietly } from "../lib/crm";
import leLogo from "../assets/le_logo.webp";
import ceLogo from "../assets/ce_logo.webp";

// ------------------------------------------------------------------
// UniThink × SAS — Program Highlights Page
// SAS Academy for Data & AI Excellence
// React (plain JS) + TailwindCSS. Mobile-first & dependency-free.
// ------------------------------------------------------------------

// Identifies this form to the CRM: becomes the campaign name and the key its
// Lead Routing rule matches on.
const CRM_FORM_ID = "sas-data-ai";

export default function DataAIProgram() {
  const ENQUIRY_ANCHOR = "#enquiry"; // keeps CTA consistent

  // simple in-page tab state for curriculum
  const tabs = ["Foundation", "Data Engineering", "Business Analytics", "AI-ML"];
  const [activeTab, setActiveTab] = useState(0);

  // enquiry form state (same simple pattern as main page)
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    city: "",
    interest: "SAS Academy for Data & AI Excellence",
    message: "",
    consent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [gotcha, setGotcha] = useState(""); // bot trap; kept out of the Sheet payload

  // handleChange (fixing checkbox + form state)
const handleChange = (e) => {
  const { name, value, type, checked } = e.target;
  setForm({
    ...form,
    [name]: type === "checkbox" ? checked : value,
  });
};

// handleSubmit (fixing setSubmitted + reset)
const handleSubmit = async (e) => {
  e.preventDefault();
  const errs = validate();
  setErrors(errs);
  if (Object.keys(errs).length > 0) return;

  try {
    // Double-writing while the CRM proves itself: the Sheet is still the
    // source of truth, so only its outcome decides what the visitor sees.
    const [sheetResult] = await Promise.allSettled([
      fetch("https://script.google.com/macros/s/AKfycbzTqu_FRYCNYuzgI3XDy41HpOCt_XgMsW2hUY9qphXnGsgmZrhLPrihqdrDkTZZMpn4/exec", {
        method: "POST",
        mode: "no-cors",
        body: JSON.stringify(form),
        headers: { "Content-Type": "application/json" },
      }),
      submitLeadQuietly({ formId: CRM_FORM_ID, fields: { ...form, _gotcha: gotcha } }),
    ]);
    if (sheetResult.status === "rejected") throw sheetResult.reason;
    setSubmitted(true);
    setForm({
      fullName: "",
      email: "",
      phone: "",
      city: "",
      interest: "SAS Academy for Data & AI Excellence",
      message: "",
      consent: false,
    });
  } catch (err) {
    alert("Error submitting form");
  }
};


  const validate = () => {
    const err = {};
    if (!form.fullName.trim()) err.fullName = "Please add your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(form.email)) err.email = "Enter a valid email";
    if (!/^[0-9+\-()\s]{8,}$/i.test(form.phone)) err.phone = "Enter a valid phone";
    if (!form.city.trim()) err.city = "Your city helps us plan batches";
    if (!form.consent) err.consent = "Please allow us to contact you";
    return err;
  };

  

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 backdrop-blur bg-white/70 border-b border-slate-200">
  <div className="max-w-6xl mx-auto px-1 py-1 flex items-center justify-between">
    
    {/* Logo (left) */}
    <img
      src="/UniThink x SAS logo.png"
      alt="SAS Logo"
      className="h-30 w-36"
    />

    {/* Enquire Button (right) */}
    <a
      href="#enquiry"
      className="hidden sm:inline-flex px-4 py-2 rounded-2xl shadow-sm bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
    >
      Enquire Now
    </a>
  </div>
</header>

      {/* Hero */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-4 pt-10 pb-12 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h1 className="text-3xl md:text-4xl font-extrabold leading-tight">SAS Academy for Data & AI Excellence</h1>
            <p className="mt-3 text-slate-700 text-base">Master Data, AI & Analytics with SAS | Certification Bootcamps for High‑Demand GenAI Roles iIndian </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#highlights" className="px-5 py-3 rounded-2xl bg-slate-900 text-white hover:bg-slate-800 font-medium shadow">Program Highlights</a>
              <a href={ENQUIRY_ANCHOR} className="px-5 py-3 rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-slate-300 font-medium shadow-sm">Enquire Now</a>
            </div>
          </div>
          <div>
            <div
  className="relative aspect-[4/3] rounded-3xl ring-1 ring-slate-200  flex items-center justify-center bg-cover bg-center"
  style={{ backgroundImage: "url('/sas_data.jpeg')" }} // image from /public folder
>
  {/* optional overlay for readability */}
  <div className="absolute inset-0 bg-black/30 rounded-3xl"></div>

</div>
          </div>
        </div>
      </section>

      {/* Intro Copy */}
      <section className="max-w-5xl mx-auto px-4 pb-4">
        <div className="space-y-4 text-slate-700 leading-relaxed">
          <p>
            The <span className="font-semibold">AI revolution</span> isn’t just coming, it’s here. <span className="font-semibold">Generative AI (GenAI)</span> is reshaping the job market at an unprecedented pace, making some skills obsolete while creating massive demand for new talent. This isn’t a challenge; it’s an opportunity.
          </p>
          <p>
            The <span className="font-semibold">SAS Academy for Data & AI Excellence</span> is your pathway to success. Our bootcamps equip you with <span className="font-semibold">job‑ready skills</span> for today’s most sought‑after roles in Data Science, Artificial Intelligence (AI), Machine Learning (ML), Data Analytics, Data Engineering, and Business Analytics. Delivered by industry experts through <span className="font-semibold">hands‑on, real‑world projects</span>, you will master essential skills in GenAI, Agentic AI, and Prompt Engineering, earning <span className="font-semibold">globally recognized certifications</span>.
          </p>
          <p>
            Stop worrying about what’s next and start building it. The SAS Academy is your <span className="font-semibold">launchpad into the future of work</span>.
          </p>
          <blockquote className="italic text-center text-slate-600 border-l-2 border-slate-200 pl-4 md:pl-0 md:border-none">
            “India will require over 1 million AI professionals by 2026, but currently, only 15–20% of the workforce is AI ready.”
            <div className="text-xs mt-1">— NASSCOM Report on AI Adoption in India</div>
          </blockquote>
        </div>
      </section>

      {/* Program Highlights */}
      <section id="highlights" className="border-y border-slate-200 bg-slate-50/40">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
            Program Highlights
          </h2>

          <div className="overflow-x-auto rounded-2xl shadow">
            <table className="w-full border-collapse text-slate-700">
              <tbody>
                <tr className="odd:bg-white even:bg-slate-50/60 hover:bg-blue-50 transition">
                  <td className="p-4 w-10 text-blue-600 font-bold">•</td>
                  <td className="p-4">
                    <span className="font-semibold">4 specialized bootcamps:</span> built for
                    high-demand careers. Designed for both freshers and professionals,
                    the Academy provides a flexible path to gain industry-recognized
                    expertise.
                  </td>
                </tr>

                <tr className="odd:bg-white even:bg-slate-50/60 hover:bg-blue-50 transition">
                  <td className="p-4 w-10 text-blue-600 font-bold">•</td>
                  <td className="p-4">
                    <span className="font-semibold">Career-Focused Tracks:</span> Foundation | Business Analytics | Data Engineering | AI & ML with GenAI, Prompt Engineering, Agentic AI, Responsible AI.
                  </td>
                </tr>

                <tr className="odd:bg-white even:bg-slate-50/60 hover:bg-blue-50 transition">
                  <td className="p-4 w-10 text-blue-600 font-bold">•</td>
                  <td className="p-4">
                    <span className="font-semibold">Global Credentials:</span> SAS Global Certifications | Digital Badges | Track Certificates.
                  </td>
                </tr>

                <tr className="odd:bg-white even:bg-slate-50/60 hover:bg-blue-50 transition">
                  <td className="p-4 w-10 text-blue-600 font-bold">•</td>
                  <td className="p-4">
                    <span className="font-semibold">Learning Support:</span> Live weekend classes • 12-month eLearning access • Session recordings • Official SAS materials • Extra lab hours • Exam prep • Flexible payments.
                  </td>
                </tr>

                <tr className="odd:bg-white even:bg-slate-50/60 hover:bg-blue-50 transition">
                  <td className="p-4 w-10 text-blue-600 font-bold">•</td>
                  <td className="p-4">
                    <span className="font-semibold">Industry Tools:</span> SAS Viya, trusted by Fortune 500 companies.
                  </td>
                </tr>

                <tr className="odd:bg-white even:bg-slate-50/60 hover:bg-blue-50 transition">
                  <td className="p-4 w-10 text-blue-600 font-bold">•</td>
                  <td className="p-4">
                    <span className="font-semibold">Real Projects:</span> Hands-on portfolio proving job readiness.
                  </td>
                </tr>

                <tr className="odd:bg-white even:bg-slate-50/60 hover:bg-blue-50 transition">
                  <td className="p-4 w-10 text-blue-600 font-bold">•</td>
                  <td className="p-4">
                    <span className="font-semibold">Proven Outcomes:</span> Alumni placed as AI Engineers, ML Developers, Data Scientists across BFSI, Healthcare, Telecom.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>


      {/* Program Tracks (cards + durations) */}
      <section id="tracks" className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-bold text-center">Program Tracks</h2>
        <p className="mt-2 text-center text-slate-600 max-w-3xl mx-auto">Discover the course that’s right for you. Start with <span className="font-medium">Foundations of Data & AI</span> for a technical base, or choose <span className="font-medium">Decision Intelligence & Business Analytics</span> for a business‑focused role. Technical paths continue to <span className="font-medium">Data Engineering</span> or <span className="font-medium">AI/ML</span>.</p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { tag: "Foundations", title: "Foundations of Data & AI", hours: "60 hrs" },
            { tag: "Engineering", title: "Applied Data Engineering for GenAI & Agentic AI", hours: "81.5 hrs" },
            { tag: "Analytics", title: "Decision Intelligence & Business Analytics", hours: "112 hrs" },
            { tag: "AI-ML", title: "Applied AI & ML with GenAI & Agentic AI", hours: "151.5 hrs" },
          ].map((t, i) => (
            <div key={i} className="rounded-3xl ring-1 ring-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">{t.tag}</div>
              <h3 className="mt-1 font-semibold">{t.title}</h3>
              <div className="mt-2 inline-flex px-2 py-1 rounded-full text-xs bg-blue-50 text-blue-700 ring-1 ring-blue-200">{t.hours}</div>
            </div>
          ))}
        </div>

        <ol className="mt-8 max-w-3xl mx-auto space-y-3 text-slate-700">
          <li><span className="font-semibold">1. Foundations of Data & AI (60 hrs)</span> — Ideal for freshers and career switchers.</li>
          <li><span className="font-semibold">2. Applied Data Engineering (81.5 hrs)</span> — Perfect for aspiring data engineers and developers.</li>
          <li><span className="font-semibold">3. Decision Intelligence & Business Analytics (112 hrs)</span> — Made for business users and analysts.</li>
          <li><span className="font-semibold">4. Applied AI & ML (151.5 hrs)</span> — Best for aspiring AI/ML professionals and data science learners.</li>
        </ol>
      </section>

      {/* Detailed Curriculum (Tabbed) */}
      <section id="curriculum" className="border-y border-slate-200 bg-slate-50/40">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="flex flex-wrap items-center gap-2">
            {tabs.map((t, i) => (
              <button
                key={t}
                onClick={() => setActiveTab(i)}
                className={`px-4 py-2 rounded-full text-sm ring-1 transition ${
                  activeTab === i ? "bg-blue-600 text-white ring-blue-600" : "bg-white text-slate-700 ring-slate-200 hover:ring-slate-300"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="mt-6 rounded-3xl ring-1 ring-slate-200 bg-white p-6 md:p-8 shadow-sm">
            {activeTab === 0 && <FoundationBlock />}
            {activeTab === 1 && <DataEngineeringBlock />}
            {activeTab === 2 && <BusinessAnalyticsBlock />}
            {activeTab === 3 && <AIMLBlock />}
          </div>
        </div>
      </section>

      {/*learn batch */}

      <section id="badges" className="max-w-6xl mx-auto px-4 py-12">
              <h2 className="text-2xl md:text-3xl font-bold">SAS Certification & Digital Badges</h2>
              <p className="mt-3 text-slate-700 max-w-3xl">
                Complete the program to earn globally recognized SAS credentials aligned with industry hiring standards. Showcase your achievement with
                verifiable digital badges on LinkedIn, resumes, and job applications.
              </p>
              <ul className="mt-4 space-y-2 text-slate-700">
                <Bullet>Earn a globally recognized SAS®️ Certification — stand out in the job market.</Bullet>
                <Bullet>Get a Digital Badge — share your skills across professional networks.</Bullet>
              </ul>
      
      
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
  <div className="flex items-start gap-4 p-5 rounded-2xl border shadow">
    <img
      src={leLogo}
      alt="Learn"
      className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 object-contain rounded-xl"
    />
    <div>
      <h3 className="text-lg font-semibold">LEARN</h3>
      <p className="text-slate-600 text-sm mt-1">
        Awarded to those who complete and pass official SAS training, across formats.
      </p>
    </div>
  </div>

  <div className="flex items-start gap-4 p-5 rounded-2xl border shadow">
    <img
      src={ceLogo}
      alt="Certified"
      className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 object-contain rounded-xl"
    />
    <div>
      <h3 className="text-lg font-semibold">CERTIFIED</h3>
      <p className="text-slate-600 text-sm mt-1">
        Awarded to those who show proficiency by passing one or more SAS certification exams.
      </p>
    </div>
  </div>
</div>

      
            </section>

      {/* Career Outcomes */}
      <section id="outcomes" className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-bold text-center">Career Outcomes</h2>
        <p className="mt-2 text-center text-slate-600">Our graduates work at top companies in India, including Infosys, TCS, Cognizant, HDFC, ICICI, IQVIA, Novartis, and Airtel.</p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <OutcomeCard
            title="Foundations of Data & AI"
            ideal="Freshers, early‑career professionals, domain experts, career switchers"
            industries="IT Services, BFSI, Retail, Healthcare"
          />
          <OutcomeCard
            title="Applied Data Engineering for GenAI & Agentic AI"
            ideal="Aspiring data engineers, backend developers, cloud & automation enthusiasts"
            industries="SaaS, Telecom, Manufacturing, AI Startups"
          />
          <OutcomeCard
            title="Decision Intelligence & Business Analytics (GenAI Era)"
            ideal="Business users, Excel/SQL analysts, consultants, PMs, non‑coding professionals"
            industries="Consulting, FMCG, Fintech, E‑commerce"
          />
          <OutcomeCard
            title="Applied AI & ML with GenAI & Agentic AI"
            ideal="Aspiring AI/ML professionals, data science learners, automation specialists"
            industries="AI Labs, Cloud Platforms, Enterprise IT, R&D Units"
          />
        </div>
      </section>

      {/* Enquiry Form */}
      <section id="enquiry" className="max-w-3xl mx-auto px-4 py-12">
        <div className="rounded-3xl ring-1 ring-slate-200 bg-white p-6 md:p-8 shadow-xl">
          <h2 className="text-2xl font-bold">Enquire Now</h2>
          <p className="mt-2 text-slate-600 text-sm">Tell us a bit about you and this program. Our counselor will reach out with schedules, fees, and next steps.</p>

          {submitted ? (
            <div className="mt-6 rounded-2xl bg-green-50 ring-1 ring-green-200 p-4">
              <p className="font-medium text-green-800">Thank you! Your enquiry has been received.</p>
              <p className="text-sm text-green-800/80">We’ll contact you shortly via phone/email.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Bot trap: hidden from people, so anything typed here is spam. */}
              <input
                type="text"
                name="_gotcha"
                value={gotcha}
                onChange={(e) => setGotcha(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              <div>
                <label className="text-sm font-medium">Full Name *</label>
                <input name="fullName" value={form.fullName} onChange={handleChange} className={`mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 ${errors.fullName ? "border-red-300 ring-red-100" : "border-slate-300 ring-blue-100"}`} placeholder="Your full name" />
                {errors.fullName ? <p className="text-xs text-red-600 mt-1">{errors.fullName}</p> : null}
              </div>
              <div>
                <label className="text-sm font-medium">Email *</label>
                <input name="email" value={form.email} onChange={handleChange} className={`mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 ${errors.email ? "border-red-300 ring-red-100" : "border-slate-300 ring-blue-100"}`} placeholder="you@example.com" />
                {errors.email ? <p className="text-xs text-red-600 mt-1">{errors.email}</p> : null}
              </div>
              <div>
                <label className="text-sm font-medium">Phone / WhatsApp *</label>
                <input name="phone" value={form.phone} onChange={handleChange} className={`mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 ${errors.phone ? "border-red-300 ring-red-100" : "border-slate-300 ring-blue-100"}`} placeholder="+91 …" />
                {errors.phone ? <p className="text-xs text-red-600 mt-1">{errors.phone}</p> : null}
              </div>
              <div>
                <label className="text-sm font-medium">City *</label>
                <input name="city" value={form.city} onChange={handleChange} className={`mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 ${errors.city ? "border-red-300 ring-red-100" : "border-slate-300 ring-blue-100"}`} placeholder="Your city" />
                {errors.city ? <p className="text-xs text-red-600 mt-1">{errors.city}</p> : null}
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Interested Program</label>
                <input name="interest" value={form.interest} onChange={handleChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Questions / Notes</label>
                <textarea name="message" value={form.message} onChange={handleChange} rows={4} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="Any specific questions (fees, schedule, certification, etc.)" />
              </div>
              <div className="md:col-span-2 flex items-start gap-3">
                <input id="consent" type="checkbox" name="consent" checked={form.consent} onChange={handleChange} className="mt-1 h-4 w-4 rounded border-slate-300" />
                <label htmlFor="consent" className="text-sm text-slate-700">I agree to be contacted by UniThink about SAS programs via phone/WhatsApp/email.</label>
              </div>
              {errors.consent ? <p className="md:col-span-2 text-xs text-red-600 -mt-2">{errors.consent}</p> : null}

              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="px-5 py-3 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-medium shadow">Submit Enquiry</button>
                <a href="#tracks" className="px-5 py-3 rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-slate-300 font-medium shadow-sm">View Tracks</a>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-8 text-sm text-slate-600 grid md:grid-cols-2 gap-4">
          <p>©️ {new Date().getFullYear()} UniThink Education International Pvt. Ltd. All rights reserved.</p>
          <p className="md:text-right">UniThink — Your Trusted SAS®️ Education Partner.</p>
        </div>
      </footer>
    </div>
  );
}

// -------
// Blocks
// -------

function Bullet({ children }) {
  return (
    <li className="flex gap-3 text-slate-700"><span className="mt-2 h-2.5 w-2.5 rounded-full bg-blue-600"></span>{children}</li>
  );
}

function FoundationBlock() {
  return (
    <div>
      <h3 className="text-xl font-semibold">Foundations of Data & AI (60 hours)</h3>
      <p className="mt-2 text-slate-700">Required first step for Data Engineering and AI‑ML pathways. Covers programming, analytics, and Responsible AI using SAS and Python. Ideal for a beginner‑level AI/ML course in India or core SAS skills for Data Science. Prepares you for certification.</p>
      <ul className="mt-4 space-y-2">
        <Bullet>Introduction to Analytics & AI</Bullet>
        <Bullet>Responsible Innovation and Trustworthy AI</Bullet>
        <Bullet>Foundational Programming for Data Analysis Using SAS</Bullet>
        <Bullet>Advanced Data Preparation and Transformation Techniques</Bullet>
        <Bullet>Fundamentals of Statistics</Bullet>
        <Bullet>Python for Data Science</Bullet>
      </ul>
      <p className="mt-4 text-sm text-slate-600"><span className="font-medium">Credentials:</span> SAS digital badges | Track Completion Certificate | Eligible for <span className="italic">SAS Certified Associate: Programming Fundamentals Using SAS 9.4</span> or <span className="italic">SAS Certified Specialist: Base SAS Programming Using SAS 9.4</span>.</p>
    </div>
  );
}

function DataEngineeringBlock() {
  return (
    <div>
      <h3 className="text-xl font-semibold">Applied Data Engineering for Generative & Agentic AI Systems (81.5 hours)</h3>
      <p className="mt-2 text-slate-700">Master scalable ETL workflows and build intelligent data pipelines — designed for professionals shaping the infrastructure behind GenAI and Agentic AI systems. Builds automation‑ready, enterprise‑grade skills using SAS Viya, Python, and low‑code tools.</p>
      <ul className="mt-4 space-y-2">
        <Bullet>Reusable Programming with SAS Macro Language for Data Automation</Bullet>
        <Bullet>Performance Optimization for Data Pipelines</Bullet>
        <Bullet>No‑Code Data Access and Transformation in SAS Studio</Bullet>
        <Bullet>Data Engineering with SAS Studio: ETL, Quality & Integration</Bullet>
        <Bullet>Low‑Code Customization in SAS Studio for Dynamic Workflows</Bullet>
        <Bullet>Visual Analytics Dashboards for Data Exploration</Bullet>
        <Bullet>Exploring Generative & Agentic AI and Prompt Engineering</Bullet>
      </ul>
      <p className="mt-4 text-sm text-slate-600"><span className="font-medium">Credentials:</span> SAS digital badges | Track Completion Certificate | Eligible for <span className="italic">SAS Certified Associate: Programming Fundamentals Using SAS Viya</span>.</p>
    </div>
  );
}

function BusinessAnalyticsBlock() {
  return (
    <div>
      <h3 className="text-xl font-semibold">Decision Intelligence & Business Analytics for the GenAI Era (112 hours)</h3>
      <p className="mt-2 text-slate-700">Built for non‑coders and analytics‑focused learners. Covers Business Analytics using SAS through hands‑on training in SAS Visual Analytics, decision logic, Responsible AI, and enterprise tools like SAS Viya.</p>

      <h4 className="mt-4 font-semibold">Level 1: Data‑Driven Business Insights with Visual Analytics & Generative AI (52.5 hours)</h4>
      <ul className="mt-2 space-y-2">
        <Bullet>Introduction to Statistical Concepts</Bullet>
        <Bullet>No‑Code Data Access and Transformation in SAS Studio</Bullet>
        <Bullet>Interactive Dashboards & Visual Reports in SAS Viya</Bullet>
        <Bullet>Exploring Generative & Agentic AI and Prompt Engineering</Bullet>
      </ul>
      <p className="mt-3 text-sm text-slate-600"><span className="font-medium">Credentials:</span> SAS digital badges | Track Completion Certificate | Eligible for <span className="italic">SAS Certified Specialist: Visual Business Analytics Using SAS Viya</span>.</p>

      <h4 className="mt-6 font-semibold">Level 2: Advanced Business Analytics with Decision Modeling & Responsible AI (59.5 hours)</h4>
      <ul className="mt-2 space-y-2">
        <Bullet>Statistics for Machine Learning</Bullet>
        <Bullet>Predictive Modeling using Visual Statistics</Bullet>
        <Bullet>Decision Automation using SAS Intelligent Decisioning</Bullet>
        <Bullet>Responsible Innovation and Trustworthy AI</Bullet>
      </ul>
      <p className="mt-3 text-sm text-slate-600"><span className="font-medium">Credentials:</span> SAS digital badges | Track Completion Certificate | Eligible for <span className="italic">SAS Certified Associate: Modeling Using SAS Visual Statistics</span>.</p>
    </div>
  );
}

function AIMLBlock() {
  return (
    <div>
      <h3 className="text-xl font-semibold">Applied Artificial Intelligence & Machine Learning with Generative & Agentic AI (151.5 hours)</h3>
      <p className="mt-2 text-slate-700">Advanced course to design, train and deploy intelligent systems using both code and visual tools. Build ML pipelines, deep learning models, and decision systems with <span className="font-medium">Generative AI, Agentic AI</span>, and <span className="font-medium">ModelOps</span>. Uses SAS Viya, Python and enterprise tools.</p>

      <h4 className="mt-4 font-semibold">Level 1: Professional Machine Learning — Statistics, Programming & Visual Insight (70 hours)</h4>
      <ul className="mt-2 space-y-2">
        <Bullet>Statistics for Machine Learning</Bullet>
        <Bullet>Performance Optimization for Data Pipelines</Bullet>
        <Bullet>Python Integration with SAS Viya for ML</Bullet>
        <Bullet>Machine Learning with SAS Viya</Bullet>
        <Bullet>Visual Analytics Dashboards for Data Exploration</Bullet>
      </ul>
      <p className="mt-3 text-sm text-slate-600"><span className="font-medium">Credentials:</span> SAS digital badges | Track Completion Certificate | Eligible for <span className="italic">SAS Certified Specialist: Machine Learning Using SAS Viya</span>.</p>

      <h4 className="mt-6 font-semibold">Level 2: Next‑Gen AI — Deep Learning, GenAI, Agentic Systems & ModelOps (81.5 hours)</h4>
      <ul className="mt-2 space-y-2">
        <Bullet>Cloud Computing Fundamentals</Bullet>
        <Bullet>Deep Learning with SAS</Bullet>
        <Bullet>Text Analytics & Natural Language Processing for Business Insights</Bullet>
        <Bullet>Generative AI Applications using SAS</Bullet>
        <Bullet>Managing Models with SAS Model Manager</Bullet>
        <Bullet>Operationalizing AI Models (ModelOps)</Bullet>
        <Bullet>Decision Automation using SAS Intelligent Decisioning</Bullet>
        <Bullet>Agentic AI with SAS Viya</Bullet>
      </ul>
      <p className="mt-3 text-sm text-slate-600">A key highlight is a <span className="font-medium">comprehensive Capstone Project</span> designed to simulate real business challenges and demonstrate your ability to contribute in real‑world scenarios.</p>
    </div>
  );
}

function OutcomeCard({ title, ideal, industries }) {
  return (
    <div className="rounded-3xl ring-1 ring-slate-200 bg-white p-6 shadow-sm">
      <h3 className="font-semibold">{title}</h3>
      <p className="mt-2 text-sm"><span className="font-medium">Ideal for:</span> {ideal}</p>
      <p className="mt-1 text-sm"><span className="font-medium">Hiring Industries:</span> {industries}</p>
    </div>
  );
}